import { Banner, Button, LoadingSpinner, Typography } from '@neo4j-ndl/react';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { PLAN_TYPE, ProjectInviteDetails } from '@nx/state';
import { consoleApi, getApiErrorMessage, useConfigurationActions, useNotifications } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { Center } from '@nx/ui';
import { type SerializedError } from '@reduxjs/toolkit';
import { type FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

const logger = createLogger(APP_SCOPE.aura);

export const AcceptInvitePage = () => {
  const { setInitialPendingInvitesShown, setPendingInvitesShown } = useConfigurationActions();
  const navigate = useNavigate();
  const location = useLocation();
  const notification = useNotifications();
  const inviteId = matchPath('/invites/:inviteId/*', location.pathname)?.params.inviteId;
  const [navigationTriggered, setNavigationTriggered] = useState(false);
  const [acceptedInviteProject, setAcceptedInviteProject] = useState<{ projectId: string; planType: PLAN_TYPE } | null>(
    null,
  );

  const [acceptInvite, acceptInviteRes] = consoleApi.useAcceptProjectInviteMutation();
  const { data: user, isFetching: isFetchingUser } = consoleApi.useGetUserDetailsQuery();
  const { data: userProjects = [] } = consoleApi.useListProjectsByUserQuery(user?.id ?? skipToken);
  const { data: userOrgs = [] } = consoleApi.useListOrganizationsByUserQuery(user?.id ?? skipToken);
  const { data: invites, isLoading: isLoadingInvites } = consoleApi.useGetUserProjectInvitesQuery(
    user?.id ?? skipToken,
  );

  const currentInvite = invites?.find((invite) => invite.id === inviteId);

  const handleAcceptInvite = (id: string, invite: ProjectInviteDetails) => {
    acceptInvite(id)
      .unwrap()
      .then(() => {
        setAcceptedInviteProject({ projectId: invite.projectId, planType: invite.planType });
        notification.addNotification({
          description: `Invitation to ${invite.projectName} accepted`,
          type: 'success',
          title: 'Invite accepted',
          timeout: 10000,
        });
      })
      .catch((err: FetchBaseQueryError | SerializedError | undefined) => {
        const message = getApiErrorMessage(err);
        logger.error(message);
        notification.addNotification({
          description: `Invite to ${invite.projectName} failed to be accepted. ${message}`,
          type: 'danger',
          title: 'Error',
          timeout: 10000,
        });
        void navigate('/', { replace: true });
      })
      .finally(() => {
        setNavigationTriggered(true);
      });
  };

  useEffect(() => {
    if (
      isNotNullish(inviteId) &&
      isNotNullish(currentInvite) &&
      !acceptInviteRes.isLoading &&
      (user?.planType === currentInvite.planType || user?.featureToggles['console-remove-plantype'] === true)
    ) {
      setPendingInvitesShown(true);
      setInitialPendingInvitesShown(true);
      handleAcceptInvite(inviteId, currentInvite);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInvite]);

  useEffect(() => {
    const p = userProjects.find((up) => up.id === acceptedInviteProject?.projectId);
    const o = userOrgs.find((org) => org.id === p?.organizationId);
    if (
      isNotNullish(p) &&
      isNotNullish(o) &&
      !isFetchingUser &&
      (user?.planType === acceptedInviteProject?.planType || user?.featureToggles['console-remove-plantype'] === true)
    ) {
      void navigate(`/projects/${p.id}/instances`);
    }
  }, [userProjects, acceptedInviteProject, navigate, isFetchingUser, userOrgs, user]);

  useEffect(() => {
    if (
      isNullish(currentInvite) &&
      isNotNullish(invites) &&
      !isLoadingInvites &&
      !navigationTriggered &&
      !isFetchingUser
    ) {
      setNavigationTriggered(true);
      notification.addNotification({
        description: `Invite ${inviteId} can not be found.`,
        type: 'info',
        title: 'Info',
        timeout: 10000,
      });
      void navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInvite, isLoadingInvites, navigationTriggered]);

  if (
    isNotNullish(inviteId) &&
    isNotNullish(currentInvite) &&
    currentInvite.planType !== user?.planType &&
    user?.featureToggles['console-remove-plantype'] !== true
  ) {
    return (
      <Center>
        <div className="bg-neutral-bg-weak flex max-w-[500px] flex-col gap-4 rounded-3xl p-6">
          <Typography variant="subheading-medium">
            You have been invited to project {currentInvite.projectName}
          </Typography>
          <Banner type="warning" hasIcon>
            Accepting a Virtual Dedicated Cloud project invitation will remove access to any self serve projects
            including any current invitations.
          </Banner>

          <div className="mt-6 flex gap-4 self-end">
            <Button
              color="neutral"
              fill="outlined"
              onClick={() => {
                void navigate('/', { replace: true });
              }}
              isDisabled={acceptInviteRes.isLoading}
            >
              Later
            </Button>
            <Button
              color="primary"
              onClick={() => handleAcceptInvite(inviteId, currentInvite)}
              isLoading={acceptInviteRes.isLoading}
            >
              Accept
            </Button>
          </div>
        </div>
      </Center>
    );
  }

  return (
    <Center>
      <LoadingSpinner size="large" />
    </Center>
  );
};
